import Swal from "sweetalert2";
import { useRouter } from "vue-router";
import { useProviderOnboardingStore } from "@/store/providerOnboardingStore";

/**
 * Composable que verifica onboarding obrigatório para usuários do tipo
 * `presential` ou `both`. Deve ser chamado logo após o login/cadastro
 * bem-sucedido.
 *
 * Prioridade dos redirecionamentos:
 *   1. Sem endereço (latitude/longitude) → /perfil
 *   2. Sem nenhum serviço ativo → /meus-servicos
 *   3. Sem disponibilidade configurada → /minha-agenda
 *
 * @returns { enforcePresentialOnboarding } função async que retorna:
 *   `true`  → redirecionou (caller deve interromper o fluxo padrão)
 *   `false` → onboarding completo (caller segue para destino normal)
 */
export function useProviderOnboardingGuard() {
  const router = useRouter();

  async function enforcePresentialOnboarding() {
    const onboardingStore = useProviderOnboardingStore();
    await onboardingStore.refresh();

    if (!onboardingStore.isPresential) return false;

    const providerType = onboardingStore.profile?.provider_type;
    const presentialLabel =
      providerType === "both" ? "Remoto e Presencial" : "Presencial";

    if (onboardingStore.needsLocation) {
      await Swal.fire({
        icon: "warning",
        title: "Complete seu endereço",
        html: `
          Seu cadastro está como <b>${presentialLabel}</b>,
          mas seu endereço de atuação ainda não foi confirmado.<br/><br/>
          Sem o endereço, você <b>não aparece nas buscas</b>. Vamos completar agora?
        `,
        confirmButtonText: "Completar agora",
        confirmButtonColor: "#b99a52",
        allowOutsideClick: false,
      });
      router.push("/perfil");
      return true;
    }

    if (onboardingStore.needsServices) {
      await Swal.fire({
        icon: "warning",
        title: "Cadastre pelo menos um serviço",
        html: `
          Para receber agendamentos, você precisa cadastrar
          <b>pelo menos um serviço ativo</b>.<br/><br/>
          Vamos cadastrar agora?
        `,
        confirmButtonText: "Cadastrar serviço",
        confirmButtonColor: "#b99a52",
        allowOutsideClick: false,
      });
      router.push("/meus-servicos");
      return true;
    }

    if (onboardingStore.needsSchedule) {
      await Swal.fire({
        icon: "warning",
        title: "Configure sua agenda",
        html: `
          Você ainda não definiu seus horários de atendimento.<br/><br/>
          Sem agenda configurada, <b>clientes não conseguem agendar</b>.
          Vamos configurar agora?
        `,
        confirmButtonText: "Configurar agenda",
        confirmButtonColor: "#b99a52",
        allowOutsideClick: false,
      });
      router.push("/minha-agenda");
      return true;
    }

    return false;
  }

  return { enforcePresentialOnboarding };
}
